import React, { lazy, Suspense, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import PropTypes from 'prop-types'
import { useLocation } from 'react-router-dom'

import { CoreContext } from 'context/CoreContext'
import useI18n from 'hooks/useI18n'
import { trackEvent } from 'services/analytics'
import { buildS3DownloadUrl } from 'services/s3Download'
import { magChatClient } from 'services/magChatClient'
import { parseStrapiImage } from 'utils'

import useMagChat from './useMagChat'
import {
  COMPETITOR_FEEDBACK_AUTO_OPEN_DELAY,
  competitorFeedbackContext,
  competitorFeedbackStorageKeys,
} from './competitorFeedback'
import {
  AssistantLayer,
  AvailabilityDot,
  AvatarFallback,
  IntroBubble,
  IntroClose,
  IntroText,
  IntroTitle,
  LauncherButton,
  LauncherImage,
  LauncherPosition,
  PanelLoading,
} from './styled'

const MagChatPanel = lazy(() => import('./MagChatPanel'))
const INTRO_LAST_SHOWN_KEY = 'mag.assistant.introLastShownAt.v1'
export const INTRO_INITIAL_DELAY = 4000
export const INTRO_DURATION = 6500
export const INTRO_REPEAT_INTERVAL = 10 * 60 * 1000
export { COMPETITOR_FEEDBACK_AUTO_OPEN_DELAY }

const configuredInterval = (name, fallback, minimum, maximum) => {
  const value = Number(process.env[name])
  if (!Number.isFinite(value)) return fallback
  return Math.min(Math.max(value, minimum), maximum)
}

function safeSessionGet(key) {
  try {
    return window.sessionStorage.getItem(key)
  } catch (_error) {
    return null
  }
}

function safeSessionSet(key, value) {
  try {
    window.sessionStorage.setItem(key, value)
  } catch (_error) {
    // The assistant remains usable when session storage is unavailable.
  }
}

function MagAssistantSession({ portal = 'universe' }) {
  const { modal, setModal, user } = useContext(CoreContext)
  const { language, t } = useI18n()
  const location = useLocation()
  const [isOpen, setIsOpen] = useState(false)
  const [showIntro, setShowIntro] = useState(false)
  const [launcherImageFailed, setLauncherImageFailed] = useState(false)
  const [isAvailable, setIsAvailable] = useState(false)
  const launcherRef = useRef(null)
  const competitorContext = useMemo(
    () => competitorFeedbackContext(location.pathname),
    [location.pathname],
  )
  const competitorContextRef = useRef(competitorContext)
  const chat = useMagChat({ competitorContext, language, t, userId: user?.id })
  const startCompetitorFeedbackRef = useRef(chat.startCompetitorFeedback)

  competitorContextRef.current = competitorContext
  startCompetitorFeedbackRef.current = chat.startCompetitorFeedback

  useEffect(() => {
    let active = true
    setIsAvailable(false)
    magChatClient.getStatus(portal, location.pathname)
      .then(available => {
        if (active) setIsAvailable(available)
      })
      .catch(() => {
        if (active) setIsAvailable(false)
      })
    return () => { active = false }
  }, [location.pathname, portal, user?.id])

  const analyticsMetadata = useCallback((extra = {}) => ({
    page_path: window.location.pathname,
    language,
    source: 'authenticated_layout',
    ...extra,
  }), [language])

  const dismissIntro = useCallback(() => {
    setShowIntro(false)
    safeSessionSet(INTRO_LAST_SHOWN_KEY, String(Date.now()))
  }, [])

  useEffect(() => {
    if (!isAvailable) return undefined
    trackEvent('mag_launcher_viewed', analyticsMetadata())
    return undefined
  }, [analyticsMetadata, isAvailable])

  useEffect(() => {
    if (
      !isAvailable
      || Boolean(competitorContext)
      || isOpen
      || modal
      || chat.messages.some(message => message.role === 'user')
    ) {
      setShowIntro(false)
      return undefined
    }

    const initialDelay = configuredInterval(
      'REACT_APP_MAG_INTRO_INITIAL_DELAY_MS',
      INTRO_INITIAL_DELAY,
      1000,
      60000,
    )
    const repeatInterval = configuredInterval(
      'REACT_APP_MAG_INTRO_REPEAT_MS',
      INTRO_REPEAT_INTERVAL,
      2 * 60 * 1000,
      60 * 60 * 1000,
    )
    const lastShownAt = Number(safeSessionGet(INTRO_LAST_SHOWN_KEY) || 0)
    const firstDelay = lastShownAt > 0
      ? Math.max(repeatInterval - (Date.now() - lastShownAt), 0)
      : initialDelay
    let hideTimer
    let repeatTimer

    const presentIntro = () => {
      setShowIntro(true)
      safeSessionSet(INTRO_LAST_SHOWN_KEY, String(Date.now()))
      hideTimer = window.setTimeout(() => setShowIntro(false), INTRO_DURATION)
      repeatTimer = window.setTimeout(presentIntro, repeatInterval)
    }

    repeatTimer = window.setTimeout(presentIntro, firstDelay)

    return () => {
      window.clearTimeout(hideTimer)
      window.clearTimeout(repeatTimer)
    }
  }, [chat.messages, competitorContext, isAvailable, isOpen, modal])

  useEffect(() => {
    const keys = competitorFeedbackStorageKeys(user?.id)
    if (!competitorContext) {
      try { window.sessionStorage.removeItem(keys.enteredAt) } catch (_error) {}
      return undefined
    }
    const storedEnteredAt = Number(safeSessionGet(keys.enteredAt) || 0)
    const enteredAt = storedEnteredAt > 0 ? storedEnteredAt : Date.now()
    if (!storedEnteredAt) safeSessionSet(keys.enteredAt, String(enteredAt))
    if (!isAvailable || safeSessionGet(keys.promptShown) === 'true') return undefined

    const markAndStart = source => {
      const currentContext = competitorContextRef.current
      if (!currentContext) return
      safeSessionSet(keys.promptShown, 'true')
      startCompetitorFeedbackRef.current(currentContext)
      setShowIntro(false)
      setIsOpen(true)
      trackEvent('competitor_feedback_prompt_shown', {
        page_path: currentContext.route,
        section: currentContext.section,
        source,
      })
    }

    if (isOpen) {
      markAndStart('manual')
      return undefined
    }
    if (modal) return undefined

    const remaining = Math.max(COMPETITOR_FEEDBACK_AUTO_OPEN_DELAY - (Date.now() - enteredAt), 0)
    const timer = window.setTimeout(() => markAndStart('auto'), remaining)
    return () => window.clearTimeout(timer)
  }, [competitorContext, isAvailable, isOpen, modal, user?.id])

  useEffect(() => {
    if (!isOpen) return undefined

    const handleEscape = event => {
      if (event.key === 'Escape') {
        event.preventDefault()
        setIsOpen(false)
        trackEvent('mag_chat_closed', analyticsMetadata({ close_method: 'escape' }))
        window.setTimeout(() => launcherRef.current?.focus(), 0)
      }
    }

    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [analyticsMetadata, isOpen])

  useEffect(() => {
    if (modal && isOpen) {
      setIsOpen(false)
      trackEvent('mag_chat_closed', analyticsMetadata({ close_method: 'global_modal' }))
    }
  }, [analyticsMetadata, isOpen, modal])

  const openChat = () => {
    dismissIntro()
    if (competitorContext) {
      const keys = competitorFeedbackStorageKeys(user?.id)
      if (safeSessionGet(keys.promptShown) !== 'true') {
        safeSessionSet(keys.promptShown, 'true')
        chat.startCompetitorFeedback(competitorContext)
        trackEvent('competitor_feedback_prompt_shown', {
          page_path: competitorContext.route,
          section: competitorContext.section,
          source: 'manual',
        })
      }
    }
    setIsOpen(true)
    trackEvent(competitorContext ? 'competitive_ai_assistant_opened' : 'mag_chat_opened', analyticsMetadata({
      competitive_section: competitorContext ? 'assistant' : undefined,
      assistant_id: competitorContext ? 'maggie' : undefined,
    }))
  }

  const closeChat = useCallback((method = 'button') => {
    setIsOpen(false)
    trackEvent('mag_chat_closed', analyticsMetadata({ close_method: method }))
    window.setTimeout(() => launcherRef.current?.focus(), 0)
  }, [analyticsMetadata])

  const handleQuickAction = (action, label) => {
    trackEvent('mag_quick_action_clicked', analyticsMetadata({
      quick_action_id: action.id,
    }))
    chat.sendMessage(label, { quickActionId: action.id })
  }

  const handleCitationClick = useCallback((event, citation) => {
    if (!citation?.url || /^\/(?:mode|trails|sub-category|competitive-intelligence)(?:\/|\?|$)/.test(citation.url)) return
    event.preventDefault()
    trackEvent(competitorContext ? 'competitive_ai_source_opened' : 'mag_citation_opened', analyticsMetadata({
      assistant_id: 'maggie',
      competitive_section: competitorContext ? 'assistant' : undefined,
      content_type: citation.contentType,
      content_id: citation.id,
      competitive_source: citation.sourceType,
    }))
    const sourceUrl = parseStrapiImage(citation.url)
    if (citation.page || String(citation.sourceType || '').startsWith('pdf')) {
      setModal?.({
        type: 'pdf-preview',
        title: citation.title,
        url: `${buildS3DownloadUrl({
          sourceUrl,
          filename: citation.title || 'preview.pdf',
          disposition: 'inline',
        })}${Number.isInteger(Number(citation.page)) && Number(citation.page) > 0 ? `#page=${Number(citation.page)}` : ''}`,
      })
      return
    }
    window.open(sourceUrl, '_blank', 'noopener,noreferrer')
  }, [analyticsMetadata, competitorContext, setModal])

  return isAvailable ? createPortal(
    <AssistantLayer
      $open={isOpen}
      data-testid="mag-assistant-layer"
      onMouseDown={event => {
        if (isOpen && event.target === event.currentTarget) closeChat('backdrop')
      }}
    >
      {isOpen && !modal ? (
        <Suspense fallback={<PanelLoading role="status">{t('mag.loading')}</PanelLoading>}>
          <MagChatPanel
            {...chat}
            firstName={String(user?.firstName || user?.name || '').trim().split(/\s+/)[0].slice(0, 40)}
            hubContext={Boolean(competitorContext)}
            onClose={() => closeChat('button')}
            onQuickAction={handleQuickAction}
            onSend={chat.sendMessage}
            onCitationClick={handleCitationClick}
            onFeedback={chat.sendFeedback}
            t={t}
          />
        </Suspense>
      ) : null}

      <LauncherPosition
        $hidden={Boolean(modal)}
        $open={isOpen}
        data-testid="mag-launcher-position"
      >
        {showIntro && !isOpen ? (
          <IntroBubble role="status">
            <IntroTitle>{t('mag.intro_title')}</IntroTitle>
            <IntroText>{t('mag.intro_body')}</IntroText>
            <IntroClose aria-label={t('mag.dismiss_intro_aria')} onClick={dismissIntro} type="button">×</IntroClose>
          </IntroBubble>
        ) : null}
        <LauncherButton
          $open={isOpen}
          aria-expanded={isOpen}
          aria-haspopup="dialog"
          aria-label={isOpen ? t('mag.close_aria') : t('mag.open_aria')}
          data-testid="mag-launcher"
          onClick={isOpen ? () => closeChat('launcher') : openChat}
          ref={launcherRef}
          title={t('mag.launcher_tooltip')}
          type="button"
        >
          {launcherImageFailed ? (
            <AvatarFallback aria-label={t('mag.avatar_fallback')}>M</AvatarFallback>
          ) : (
            <LauncherImage
              alt=""
              data-testid="mag-launcher-image"
              onError={() => setLauncherImageFailed(true)}
              src="/mag/mag-launcher-transparent.png"
            />
          )}
          <AvailabilityDot aria-hidden="true" />
        </LauncherButton>
      </LauncherPosition>
    </AssistantLayer>,
    document.body,
  ) : null
}

export default function MagAssistant({ portal = 'universe' }) {
  const { user } = useContext(CoreContext)
  return user?.id ? <MagAssistantSession key={user.id} portal={portal} /> : null
}

MagAssistant.propTypes = {
  portal: PropTypes.string,
}
